class CartDrawerReebok extends HTMLElement {
  connectedCallback() {
    this._body = this.querySelector('[data-cart-drawer-body]');
    this._sectionId = this.dataset.sectionId || 'cart-drawer-reebok';
    this._protectionProductId = parseInt(this.dataset.protectionProductId || '0', 10);
    this._buildTimer = null;
    this._isFetching = false;

    this._onCartBuild = this._scheduleBuild.bind(this);
    document.addEventListener('cart:build', this._onCartBuild);

    this._bindBodyEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:build', this._onCartBuild);
    clearTimeout(this._buildTimer);
  }

  _scheduleBuild() {
    clearTimeout(this._buildTimer);
    this._buildTimer = setTimeout(() => this._build(), 150);
  }

  async _build() {
    if (this._isFetching) return;
    this._isFetching = true;
    this._setLoading(true);

    try {
      let newBody = await this._fetchSectionBody();
      if (!newBody) return;

      const protQty = parseInt(newBody.dataset.protectionQty || '0', 10);
      const protKey = newBody.dataset.protectionKey || '';

      if (protQty > 1 && protKey) {
        await this._capProtectionSilent(protKey);
        newBody = await this._fetchSectionBody();
        if (!newBody) return;
      }

      this._body.innerHTML = newBody.innerHTML;
      this._body.dataset.cartItemCount = newBody.dataset.cartItemCount || '0';
      this._body.dataset.protectionKey = newBody.dataset.protectionKey || '';
      this._body.dataset.protectionQty = newBody.dataset.protectionQty || '0';
      this._bindBodyEvents();
      this._syncCartCount();
    } catch (err) {
      console.error('[CartDrawerReebok] Build error:', err);
    } finally {
      this._isFetching = false;
      this._setLoading(false);
    }
  }

  async _fetchSectionBody() {
    const res = await fetch(`/cart?sections=${this._sectionId}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!res.ok) throw new Error(`Sections fetch failed: ${res.status}`);
    const data = await res.json();
    const rawHtml = data[this._sectionId];
    if (!rawHtml) throw new Error('Section HTML not found');
    const doc = new DOMParser().parseFromString(rawHtml, 'text/html');
    return doc.querySelector('[data-cart-drawer-body]');
  }

  async _capProtectionSilent(key) {
    await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity: 1 })
    });
  }

  _bindBodyEvents() {
    if (!this._body) return;

    this._body.querySelectorAll('[data-remove-item]').forEach(btn => {
      const fresh = btn.cloneNode(true);
      btn.replaceWith(fresh);
      fresh.addEventListener('click', () => this._removeItem(fresh.dataset.removeItem));
    });

    this._body.querySelectorAll('[data-readd-protection]').forEach(btn => {
      const fresh = btn.cloneNode(true);
      btn.replaceWith(fresh);
      fresh.addEventListener('click', () => this._reAddProtection(fresh.dataset.readdProtection));
    });
  }

  _syncCartCount() {
    const count = parseInt(this._body?.dataset.cartItemCount || '0', 10);
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count > 0 ? String(count) : '0';
    });
  }

  _setLoading(active) {
    this._body?.classList.toggle('cart-drawer-reebok__body--loading', active);
  }

  async _removeItem(key) {
    if (!key) return;
    this._setLoading(true);

    try {
      const res = await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!res.ok) throw new Error(`Remove failed: ${res.status}`);

      const cart = await res.json();

      await this._syncProtection(cart);

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart }, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    } catch (err) {
      console.error('[CartDrawerReebok] Remove error:', err);
      this._setLoading(false);
    }
  }

  _isProtectionItem(item) {
    if (this._protectionProductId && item.product_id === this._protectionProductId) return true;
    return item.properties && item.properties._package_protection === 'true';
  }

  _isSizerItem(item) {
    const sizerVariants = (typeof ringSizerProductVariants !== 'undefined' && Array.isArray(ringSizerProductVariants))
      ? ringSizerProductVariants
      : (window.ringSizerProductVariants || []);
    return sizerVariants.includes(item.variant_id);
  }

  async _syncProtection(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    const protectionItem = cart.items.find(item => this._isProtectionItem(item));
    if (!protectionItem) return;

    const hasMainItems = cart.items.some(item => !this._isProtectionItem(item) && !this._isSizerItem(item));
    if (hasMainItems) return;

    await fetch(window.theme?.routes?.cartChange || '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: protectionItem.key, quantity: 0 })
    });
  }

  async _reAddProtection(variantId) {
    if (!variantId) return;
    this._setLoading(true);

    try {
      const res = await fetch(window.theme?.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _package_protection: 'true' } }]
        })
      });

      if (!res.ok) throw new Error(`Re-add failed: ${res.status}`);

      document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
    } catch (err) {
      console.error('[CartDrawerReebok] Re-add error:', err);
      this._setLoading(false);
    }
  }
}

if (!customElements.get('cart-drawer-reebok')) {
  customElements.define('cart-drawer-reebok', CartDrawerReebok);
}
